import React, { useState, useEffect } from 'react';
import { formatPrice } from '../utils/currency';
import { toast } from 'sonner';
import TestPaymentModal from './TestPaymentModal';

const AdminDashboard = ({ apiClient, userId, onLogout }) => {
  const [currentUserEmail, setCurrentUserEmail] = useState(null);
  const [activeTab, setActiveTab] = useState('analytics');
  const [showTestPayment, setShowTestPayment] = useState(false);
  const [analytics, setAnalytics] = useState({});
  const [restaurants, setRestaurants] = useState([]);
  const [adminConfig, setAdminConfig] = useState({});
  const [adminUsers, setAdminUsers] = useState([]);
  const [protectedUsers, setProtectedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingConfig, setEditingConfig] = useState({
    contact: false,
    stripe: false,
    email: false
  });
  const [showAddAdmin, setShowAddAdmin] = useState(false);
  const [configFormData, setConfigFormData] = useState({
    contact_email: '',
    contact_phone: '',
    stripe_publishable_key: '',
    stripe_secret_key: '',
    gmail_username: '',
    gmail_app_password: ''
  });

  // Deployment state
  const [deploymentStatus, setDeploymentStatus] = useState({
    indexing: false,
    progress: '',
    results: null,
    error: null
  });
  const [showIndexingConfirm, setShowIndexingConfirm] = useState(false);
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [isProtectedUser, setIsProtectedUser] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [userToRemove, setUserToRemove] = useState(null);

  useEffect(() => {
    fetchDashboardData();
    getCurrentUserEmail();
  }, []);

  const getCurrentUserEmail = () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        // Decode JWT token to get user email
        const payload = JSON.parse(atob(token.split('.')[1]));
        setCurrentUserEmail(payload.email);
      } catch (error) {
        console.error('Error decoding token:', error);
      }
    }
  };

  const fetchDashboardData = async () => {
    try {
      // Fetch all admin dashboard data from proper endpoints
      const [configResponse, adminUsersResponse, analyticsResponse, restaurantsResponse] = await Promise.all([
        apiClient.get('/admin/config'),
        apiClient.get('/admin/users'),
        apiClient.get('/admin/analytics'),
        apiClient.get('/admin/restaurants')
      ]);
      
      setAdminConfig(configResponse.data);
      setAdminUsers(adminUsersResponse.data.admin_users || []);
      setProtectedUsers(adminUsersResponse.data.protected_users || []);
      setAnalytics(analyticsResponse.data);
      setRestaurants(restaurantsResponse.data.restaurants || []);
      
      setConfigFormData({
        contact_email: configResponse.data.contact_email || '',
        contact_phone: configResponse.data.contact_phone || '',
        stripe_publishable_key: configResponse.data.stripe_publishable_key || '',
        stripe_secret_key: configResponse.data.stripe_secret_key || '',
        gmail_username: configResponse.data.gmail_username || '',
        gmail_app_password: configResponse.data.gmail_app_password || ''
      });
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/admin/users', { 
        email: newAdminEmail,
        is_protected: isProtectedUser 
      });
      toast.success('Admin user added successfully!');
      setNewAdminEmail('');
      setIsProtectedUser(false);
      setShowAddAdmin(false);
      fetchDashboardData();
    } catch (error) {
      console.error('Error adding admin:', error);
      toast.error(error.response?.data?.detail || 'Failed to add admin user');
    }
  };

  const handleRemoveAdmin = (adminEmail) => {
    setUserToRemove(adminEmail);
    setShowRemoveConfirm(true);
  };

  const confirmRemoveAdmin = async () => {
    if (!userToRemove) return;
    
    try {
      await apiClient.delete(`/admin/users/${encodeURIComponent(userToRemove)}`);
      toast.success('Admin user removed successfully!');
      fetchDashboardData();
    } catch (error) {
      console.error('Error removing admin:', error);
      toast.error(error.response?.data?.detail || 'Failed to remove admin user');
    }
    
    setShowRemoveConfirm(false);
    setUserToRemove(null);
  };

  const cancelRemoveAdmin = () => {
    setShowRemoveConfirm(false);
    setUserToRemove(null);
  };

  const handleConfigSubmit = async (section) => {
    try {
      const response = await apiClient.post('/admin/config', configFormData);
      console.log('Config save response:', response.data);
      
      let sectionName = '';
      switch(section) {
        case 'contact':
          sectionName = 'Contact details';
          break;
        case 'stripe':
          sectionName = 'Stripe configuration';
          break;
        case 'email':
          sectionName = 'Email configuration';
          break;
      }
      
      toast.success(`${sectionName} updated successfully!`);
      setEditingConfig(prev => ({ ...prev, [section]: false }));
      
      // Force refresh with a small delay to ensure backend is updated
      setTimeout(async () => {
        console.log('Refreshing dashboard data after config save...');
        await fetchDashboardData();
      }, 500);
    } catch (error) {
      console.error('Error updating config:', error);
      toast.error('Failed to update configuration');
    }
  };

  const cancelEdit = (section) => {
    setEditingConfig(prev => ({ ...prev, [section]: false }));
    // Reset form data to original values
    setConfigFormData({
      contact_email: adminConfig.contact_email || '',
      contact_phone: adminConfig.contact_phone || '',
      stripe_publishable_key: adminConfig.stripe_publishable_key || '',
      stripe_secret_key: adminConfig.stripe_secret_key || '',
      gmail_username: adminConfig.gmail_username || '',
      gmail_app_password: adminConfig.gmail_app_password || ''
    });
  };

  // Deployment functions
  const handleIndexingButtonClick = () => {
    setShowIndexingConfirm(true);
  };

  const runProductionIndexing = async () => {
    setShowIndexingConfirm(false);

    setDeploymentStatus({
      indexing: true,
      progress: 'Initializing production database indexing...',
      results: null,
      error: null
    });

    try {
      const response = await apiClient.post('/admin/deployment/create-indexes');
      
      setDeploymentStatus({
        indexing: false,
        progress: 'Completed',
        results: response.data,
        error: null
      });

      toast.success('🎉 Production database indexing completed successfully!');
      
    } catch (error) {
      console.error('Production indexing error:', error);
      
      setDeploymentStatus({
        indexing: false,
        progress: 'Failed',
        results: null,
        error: error.response?.data?.detail || error.message || 'Unknown error occurred'
      });

      toast.error('❌ Production indexing failed. Check deployment tab for details.');
    }
  };

  const cancelIndexing = () => {
    setShowIndexingConfirm(false);
  };

  const resetDeploymentStatus = () => {
    setDeploymentStatus({
      indexing: false,
      progress: '',
      results: null,
      error: null
    });
  };

  const handleConfigInputChange = (e) => {
    setConfigFormData({
      ...configFormData,
      [e.target.name]: e.target.value
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mx-auto mb-4" style={{ width: '32px', height: '32px' }}></div>
          <p className="text-gray-600">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'analytics', name: 'Analytics', icon: '📊' },
    { id: 'restaurants', name: 'Restaurants', icon: '🏪' },
    { id: 'admins', name: 'Admin Users', icon: '👥' },
    { id: 'config', name: 'Configuration', icon: '⚙️' },
    { id: 'deployment', name: 'Deployment', icon: '🚀' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <h1 className="text-xl font-semibold text-gray-900">
                Admin Dashboard
              </h1>
              <span className="px-3 py-1 bg-red-100 text-red-800 text-sm font-medium rounded-full">
                Administrator
              </span>
            </div>
            
            <button
              onClick={onLogout}
              data-testid="admin-logout-button"
              className="text-gray-500 hover:text-gray-700 text-sm font-medium"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tab Navigation */}
        <div className="bg-white rounded-lg shadow-sm mb-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex flex-wrap gap-x-2 gap-y-1 sm:gap-x-8 sm:gap-y-0 px-4 sm:px-6">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  data-testid={`admin-${tab.id}-tab`}
                  className={`py-2 sm:py-4 px-2 sm:px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap flex items-center ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <span className="mr-1 sm:mr-2 text-sm">{tab.icon}</span>
                  <span className="hidden sm:inline">{tab.name}</span>
                  <span className="sm:hidden text-xs">{tab.name.replace(' Users', '').replace(' Management', '')}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="fade-in">
          {activeTab === 'analytics' && (
            <div className="space-y-6">
              {/* Analytics Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="card text-center">
                  <h3 className="text-sm font-medium text-gray-500">Total Restaurants</h3>
                  <p className="text-3xl font-bold text-gray-900 mt-2" data-testid="total-restaurants">
                    {analytics.total_restaurants || 0}
                  </p>
                </div>
                <div className="card text-center">
                  <h3 className="text-sm font-medium text-gray-500">Active Restaurants</h3>
                  <p className="text-3xl font-bold text-green-600 mt-2" data-testid="active-restaurants">
                    {analytics.active_restaurants || 0}
                  </p>
                </div>
                <div className="card text-center">
                  <h3 className="text-sm font-medium text-gray-500">Total Orders</h3>
                  <p className="text-3xl font-bold text-blue-600 mt-2" data-testid="total-orders">
                    {analytics.total_orders || 0}
                  </p>
                </div>
                <div className="card text-center">
                  <h3 className="text-sm font-medium text-gray-500">Total Revenue</h3>
                  <p className="text-3xl font-bold text-purple-600 mt-2" data-testid="total-revenue">
                    {formatPrice(analytics.total_revenue || 0, 'USD')}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="card">
                  <h3 className="text-lg font-medium mb-4">Capacity Statistics</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Restaurants with Capacity:</span>
                      <span className="font-medium" data-testid="restaurants-with-capacity">
                        {analytics.restaurants_with_capacity || 0}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Capacity Purchases:</span>
                      <span className="font-medium" data-testid="capacity-purchases-count">
                        {analytics.capacity_purchases_count || 0}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total Capacity Sold:</span>
                      <span className="font-medium" data-testid="total-capacity-sold">
                        {analytics.total_capacity_sold || 0} orders
                      </span>
                    </div>
                  </div>
                </div>

                <div className="card">
                  <h3 className="text-lg font-medium mb-4">Recent Activity</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Orders (Last 7 Days):</span>
                      <span className="font-medium" data-testid="recent-orders">
                        {analytics.recent_activity?.orders_last_7_days || 0}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">New Registrations (7 Days):</span>
                      <span className="font-medium" data-testid="recent-registrations">
                        {analytics.recent_activity?.registrations_last_7_days || 0}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Capacity Purchases (7 Days):</span>
                      <span className="font-medium" data-testid="recent-capacity-purchases">
                        {analytics.recent_activity?.capacity_purchases_last_7_days || 0}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Capacity Revenue (7 Days):</span>
                      <span className="font-medium text-green-600" data-testid="recent-capacity-revenue">
                        {formatPrice(analytics.recent_activity?.capacity_value_last_7_days || 0, 'USD')}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="card">
                  <h3 className="text-lg font-medium mb-4">System Health</h3>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">System Status:</span>
                      <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                        {analytics.system_health?.status || 'Online'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Last Updated:</span>
                      <span className="text-sm font-medium">
                        {analytics.system_health?.last_updated ? 
                          new Date(analytics.system_health.last_updated).toLocaleString() : 
                          new Date().toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'restaurants' && (
            <div className="card">
              <div className="card-header">
                <h2 className="text-xl font-semibold text-gray-900">Restaurant Management</h2>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Restaurant
                      </th>
                      <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Contact
                      </th>
                      <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Capacity
                      </th>
                      <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Stats
                      </th>
                      <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {restaurants.map((restaurant) => (
                      <tr key={restaurant.id} data-testid={`restaurant-row-${restaurant.id}`}>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900" data-testid={`restaurant-name-${restaurant.id}`}>
                              {restaurant.name}
                            </div>
                            <div className="text-xs sm:text-sm text-gray-500">
                              {restaurant.address}
                            </div>
                          </div>
                        </td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                          <div className="text-xs sm:text-sm text-gray-900">{restaurant.email}</div>
                          <div className="text-xs sm:text-sm text-gray-500">{restaurant.phone}</div>
                        </td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                          <span 
                            className={`px-2 py-1 rounded-full text-sm font-medium ${
                              restaurant.order_capacity > 10 
                                ? 'bg-green-100 text-green-800' 
                                : restaurant.order_capacity > 0 
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-red-100 text-red-800'
                            }`}
                            data-testid={`restaurant-capacity-${restaurant.id}`}
                          >
                            {restaurant.order_capacity}
                          </span>
                        </td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                          <div className="space-y-1">
                            <div className="flex justify-between text-xs sm:text-sm">
                              <span className="text-gray-500">Orders:</span>
                              <span className="font-medium" data-testid={`total-orders-${restaurant.id}`}>
                                {restaurant.total_orders || 0}
                              </span>
                            </div>
                            <div className="flex justify-between text-xs sm:text-sm">
                              <span className="text-gray-500">Recent:</span>
                              <span className="font-medium text-blue-600" data-testid={`recent-orders-${restaurant.id}`}>
                                {restaurant.recent_orders || 0}
                              </span>
                            </div>
                            <div className="flex justify-between text-xs sm:text-sm">
                              <span className="text-gray-500">Capacity:</span>
                              <span className="font-medium text-green-600" data-testid={`capacity-purchased-${restaurant.id}`}>
                                {restaurant.total_capacity_purchased || 0}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                          <div className="space-y-2">
                            <span 
                              className={`px-2 py-1 rounded-full text-sm font-medium ${
                                restaurant.is_active 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-red-100 text-red-800'
                              }`}
                              data-testid={`restaurant-status-${restaurant.id}`}
                            >
                              {restaurant.status || (restaurant.is_active ? 'Active' : 'Inactive')}
                            </span>
                            <div className="text-xs text-gray-500">
                              <div>Joined: {restaurant.created_at ? new Date(restaurant.created_at).toLocaleDateString() : 'N/A'}</div>
                              <div>Stripe: {restaurant.stripe_configured ? '✅ Configured' : '❌ Not Setup'}</div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {restaurants.length === 0 && (
                <div className="text-center py-8 text-gray-500" data-testid="no-restaurants-message">
                  No restaurants registered yet.
                </div>
              )}
            </div>
          )}

          {activeTab === 'admins' && (
            <div className="card">
              <div className="card-header">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-semibold text-gray-900">Admin User Management</h2>
                  <button
                    onClick={() => setShowAddAdmin(true)}
                    data-testid="add-admin-user-button"
                    className="btn-primary"
                  >
                    Add Admin User
                  </button>
                </div>
              </div>

              {/* Add Admin Form */}
              {showAddAdmin && (
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <h3 className="text-lg font-medium mb-4">Add New Admin User</h3>
                  <form onSubmit={handleAddAdmin} className="space-y-4">
                    <div>
                      <label htmlFor="admin_email" className="form-label">
                        Email Address
                      </label>
                      <input
                        type="email"
                        id="admin_email"
                        value={newAdminEmail}
                        onChange={(e) => setNewAdminEmail(e.target.value)}
                        data-testid="new-admin-email-input"
                        className="form-input"
                        placeholder="Enter admin email address"
                        required
                      />
                    </div>
                    
                    {/* Protected User Checkbox */}
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="is_protected"
                        checked={isProtectedUser}
                        onChange={(e) => setIsProtectedUser(e.target.checked)}
                        data-testid="protected-user-checkbox"
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                      />
                      <label htmlFor="is_protected" className="text-sm font-medium text-gray-700">
                        Mark as Protected User
                      </label>
                    </div>
                    <p className="text-xs text-gray-500 -mt-2">
                      Protected users cannot be removed by other administrators and are essential for system security.
                    </p>
                    
                    <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
                      <button
                        type="submit"
                        data-testid="save-admin-user-button"
                        className="btn-primary"
                      >
                        Add Admin User
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowAddAdmin(false);
                          setNewAdminEmail('');
                          setIsProtectedUser(false);
                        }}
                        data-testid="cancel-add-admin-button"
                        className="btn-secondary"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Admin Users List */}
              <div className="space-y-4">
                {adminUsers.length === 0 ? (
                  <div className="text-center py-8 text-gray-500" data-testid="no-admin-users-message">
                    No admin users found.
                  </div>
                ) : (
                  adminUsers.map((admin) => (
                    <div
                      key={admin.id}
                      className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 space-y-2 sm:space-y-0"
                      data-testid={`admin-user-item-${admin.id}`}
                    >
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900" data-testid={`admin-email-${admin.id}`}>
                          {admin.email}
                        </h3>
                        <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-4 mt-1">
                          <span 
                            className={`px-2 py-1 rounded-full text-sm font-medium ${
                              admin.is_active 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}
                            data-testid={`admin-status-${admin.id}`}
                          >
                            {admin.is_active ? 'Active' : 'Inactive'}
                          </span>
                          <span className="text-sm text-gray-500">
                            Added: {new Date(admin.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        {(() => {
                          // Use dynamic protected users from API
                          
                          const isCurrentUser = admin.email === currentUserEmail;
                          const isProtectedUser = protectedUsers.includes(admin.email);
                          
                          if (isCurrentUser) {
                            return <span className="text-sm text-blue-600 font-medium">You</span>;
                          } else if (isProtectedUser) {
                            return <span className="text-sm text-gray-500 font-medium">Protected</span>;
                          } else {
                            return (
                              <button
                                onClick={() => handleRemoveAdmin(admin.email)}
                                data-testid={`remove-admin-${admin.email}`}
                                className="px-3 py-1 text-sm text-red-600 hover:text-red-800"
                              >
                                Remove
                              </button>
                            );
                          }
                        })()}
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-medium text-blue-900 mb-2">Admin Access Information</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• Admin users can access the admin dashboard with any password (currently: admin123)</li>
                  <li>• Only authorized email addresses can log in as admin</li>
                  <li>• Admin users can manage restaurants, view analytics, and configure system settings</li>
                  <li>• You cannot remove yourself from the admin list</li>
                </ul>
              </div>
            </div>
          )}

          {activeTab === 'config' && (
            <div className="space-y-6">
              {/* Header */}
              <div className="card">
                <div className="card-header">
                  <h2 className="text-xl font-semibold text-gray-900">System Configuration</h2>
                  <p className="text-gray-600 text-sm mt-1">Manage different aspects of your system configuration separately</p>
                </div>
              </div>

              {/* Contact Details Section */}
              <div className="card">
                <div className="card-header">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 flex items-center">
                        <span className="mr-2">📞</span>
                        Contact Details
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">Contact information displayed to restaurants</p>
                    </div>
                    {!editingConfig.contact && (
                      <button
                        onClick={() => setEditingConfig(prev => ({ ...prev, contact: true }))}
                        data-testid="edit-contact-button"
                        className="btn-primary"
                      >
                        Edit Contact Details
                      </button>
                    )}
                  </div>
                </div>

                {editingConfig.contact ? (
                  <form onSubmit={(e) => { e.preventDefault(); handleConfigSubmit('contact'); }} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="contact_email" className="form-label">
                          Contact Email
                        </label>
                        <input
                          type="email"
                          id="contact_email"
                          name="contact_email"
                          value={configFormData.contact_email}
                          onChange={handleConfigInputChange}
                          data-testid="config-contact-email-input"
                          className="form-input"
                          placeholder="support@restaurant.com"
                          required
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          This email will be displayed to restaurants for support
                        </p>
                      </div>

                      <div>
                        <label htmlFor="contact_phone" className="form-label">
                          Contact Phone
                        </label>
                        <input
                          type="tel"
                          id="contact_phone"
                          name="contact_phone"
                          value={configFormData.contact_phone}
                          onChange={handleConfigInputChange}
                          data-testid="config-contact-phone-input"
                          className="form-input"
                          placeholder="+1 (555) 123-4567"
                          required
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Support phone number for restaurants
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
                      <button
                        type="submit"
                        data-testid="save-contact-button"
                        className="btn-primary"
                      >
                        Save Contact Details
                      </button>
                      <button
                        type="button"
                        onClick={() => cancelEdit('contact')}
                        data-testid="cancel-contact-button"
                        className="btn-secondary"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="form-label">Contact Email</label>
                      <p className="text-gray-900" data-testid="config-contact-email-display">
                        {adminConfig.contact_email || (
                          <span className="text-gray-500 italic">Not configured</span>
                        )}
                      </p>
                    </div>

                    <div>
                      <label className="form-label">Contact Phone</label>
                      <p className="text-gray-900" data-testid="config-contact-phone-display">
                        {adminConfig.contact_phone || (
                          <span className="text-gray-500 italic">Not configured</span>
                        )}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Stripe Configuration Section */}
              <div className="card">
                <div className="card-header">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 flex items-center">
                        <span className="mr-2">💳</span>
                        Stripe Configuration
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">Payment processing configuration for restaurants</p>
                    </div>
                    {!editingConfig.stripe && (
                      <button
                        onClick={() => setEditingConfig(prev => ({ ...prev, stripe: true }))}
                        data-testid="edit-stripe-button"
                        className="btn-primary"
                      >
                        Edit Stripe Config
                      </button>
                    )}
                  </div>
                </div>

                {editingConfig.stripe ? (
                  <form onSubmit={(e) => { e.preventDefault(); handleConfigSubmit('stripe'); }} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="stripe_publishable_key" className="form-label">
                          Stripe Publishable Key
                        </label>
                        <input
                          type="text"
                          id="stripe_publishable_key"
                          name="stripe_publishable_key"
                          value={configFormData.stripe_publishable_key}
                          onChange={handleConfigInputChange}
                          data-testid="config-stripe-publishable-key-input"
                          className="form-input"
                          placeholder="pk_live_..."
                          required
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Publishable key (safe to expose in frontend)
                        </p>
                      </div>

                      <div>
                        <label htmlFor="stripe_secret_key" className="form-label">
                          Stripe Restricted Key (rk_)
                        </label>
                        <input
                          type="password"
                          id="stripe_secret_key"
                          name="stripe_secret_key"
                          value={configFormData.stripe_secret_key}
                          onChange={handleConfigInputChange}
                          data-testid="config-stripe-secret-key-input"
                          className="form-input"
                          placeholder="rk_live_..."
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Only a Stripe <strong>Restricted key</strong> (starts with rk_) is accepted.
                          It needs write access to Payment Intents. Keep secure! Leave empty to keep existing key.
                        </p>
                      </div>
                    </div>

                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <span className="text-yellow-600">⚠️</span>
                        </div>
                        <div className="ml-3">
                          <h4 className="text-sm font-medium text-yellow-800">Important Security Notice</h4>
                          <p className="mt-1 text-sm text-yellow-700">
                            These keys will be used by all restaurants for payment processing. Make sure you are using <strong>Live mode</strong> keys.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-testid="admin-stripe-help">
                      <h4 className="text-sm font-medium text-blue-900 mb-2">How to get these keys</h4>

                      <div className="bg-amber-50 border border-amber-300 rounded-md p-3 mb-3" data-testid="admin-stripe-livemode-note">
                        <p className="text-sm text-amber-900">
                          <strong>Use Live mode keys</strong> to accept real payments. Switch your Stripe
                          Dashboard to <strong>Live mode</strong> using the top-right toggle before copying.
                          Live keys start with <code>pk_live_</code> and <code>rk_live_</code>.
                        </p>
                      </div>

                      <p className="text-sm font-semibold text-blue-900">1. Switch to Live mode</p>
                      <ol className="text-sm text-blue-800 space-y-1 list-decimal ml-5">
                        <li>In the <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="underline font-medium">Stripe Dashboard</a>, switch to <strong>Live mode</strong> using the top-right toggle.</li>
                      </ol>
                      <p className="text-sm font-semibold text-blue-900 mt-2">2. Publishable key (pk_live_)</p>
                      <ol className="text-sm text-blue-800 space-y-1 list-decimal ml-5">
                        <li>Open <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="underline font-medium">Developers → API keys</a> and copy the <strong>Publishable key</strong> (starts with <code>pk_live_</code>).</li>
                      </ol>
                      <p className="text-sm font-semibold text-blue-900 mt-2">3. Restricted key (rk_live_) — required</p>
                      <ol className="text-sm text-blue-800 space-y-1 list-decimal ml-5">
                        <li>Go to <a href="https://dashboard.stripe.com/apikeys/create" target="_blank" rel="noopener noreferrer" className="underline font-medium">Create restricted key</a> (in Live mode).</li>
                        <li>Set <strong>Payment Intents → Write</strong> (leave everything else as None).</li>
                        <li>Click <strong>Create key</strong> and copy the key (starts with <code>rk_live_</code>).</li>
                      </ol>
                      <p className="text-xs text-blue-800 mt-2 bg-blue-100 border border-blue-200 rounded-md p-2" data-testid="admin-stripe-leastprivilege-note">
                        By granting <strong>only Payment Intents → Write</strong>, this key can be used solely to
                        accept payments into your Stripe account. It <strong>cannot</strong> issue refunds, view
                        customers or balance, move money, or change settings — keeping your account fully secure.
                      </p>
                      <p className="text-xs text-blue-700 mt-2">
                        See the <a href="https://docs.stripe.com/keys#limit-access-to-a-restricted-api-key" target="_blank" rel="noopener noreferrer" className="underline font-medium">Stripe restricted keys guide</a>.
                      </p>
                    </div>

                    <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
                      <button
                        type="submit"
                        data-testid="save-stripe-button"
                        className="btn-primary"
                      >
                        Save Stripe Configuration
                      </button>
                      <button
                        type="button"
                        onClick={() => cancelEdit('stripe')}
                        data-testid="cancel-stripe-button"
                        className="btn-secondary"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="form-label">Stripe Publishable Key</label>
                      <p className="text-gray-900 font-mono text-sm break-all" data-testid="config-stripe-publishable-key-display">
                        {adminConfig.stripe_publishable_key || (
                          <span className="text-gray-500 italic font-sans">Not configured</span>
                        )}
                      </p>
                    </div>

                    <div>
                      <label className="form-label">Stripe Restricted Key (rk_)</label>
                      <p className="text-gray-900 font-mono text-sm" data-testid="config-stripe-secret-key-display">
                        {adminConfig.stripe_secret_key ? (
                          <span>
                            {adminConfig.stripe_secret_key.substring(0, 12)}...
                            <span className="text-green-600 ml-2">✓ Configured</span>
                          </span>
                        ) : (
                          <span className="text-gray-500 italic font-sans">Not configured</span>
                        )}
                      </p>
                    </div>
                  </div>
                )}
                {!editingConfig.stripe && adminConfig.stripe_publishable_key && adminConfig.stripe_secret_key && (
                  <div className="mt-4">
                    <button
                      onClick={() => setShowTestPayment(true)}
                      data-testid="admin-test-payment-button"
                      className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
                      title="Charge 1 USD to verify the platform Stripe keys work"
                    >
                      Test Payment (1 USD)
                    </button>
                  </div>
                )}
              </div>

              {/* Email Configuration Section */}
              <div className="card">
                <div className="card-header">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 flex items-center">
                        <span className="mr-2">📧</span>
                        Notification Mail Configuration
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">Gmail settings for sending system notifications and password resets</p>
                    </div>
                    {!editingConfig.email && (
                      <button
                        onClick={() => setEditingConfig(prev => ({ ...prev, email: true }))}
                        data-testid="edit-email-button"
                        className="btn-primary"
                      >
                        Edit Email Config
                      </button>
                    )}
                  </div>
                </div>

                {editingConfig.email ? (
                  <form onSubmit={(e) => { e.preventDefault(); handleConfigSubmit('email'); }} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="gmail_username" className="form-label">
                          Gmail Username
                        </label>
                        <input
                          type="email"
                          id="gmail_username"
                          name="gmail_username"
                          value={configFormData.gmail_username}
                          onChange={handleConfigInputChange}
                          data-testid="config-gmail-username-input"
                          className="form-input"
                          placeholder="notifications@yourdomain.com"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Gmail address used for sending system emails
                        </p>
                      </div>

                      <div>
                        <label htmlFor="gmail_app_password" className="form-label">
                          Gmail App Password
                        </label>
                        <input
                          type="password"
                          id="gmail_app_password"
                          name="gmail_app_password"
                          value={configFormData.gmail_app_password}
                          onChange={handleConfigInputChange}
                          data-testid="config-gmail-app-password-input"
                          className="form-input"
                          placeholder="16-character app password"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Gmail App Password (not your regular password). 
                          <a 
                            href="https://support.google.com/accounts/answer/185833" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-700 underline ml-1"
                          >
                            How to create one
                          </a>
                        </p>
                      </div>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <span className="text-blue-600">ℹ️</span>
                        </div>
                        <div className="ml-3">
                          <h4 className="text-sm font-medium text-blue-800">Email Configuration Help</h4>
                          <ul className="mt-1 text-sm text-blue-700 space-y-1">
                            <li>• Enable 2FA on your Gmail account first</li>
                            <li>• Generate an App Password in Gmail Security settings</li>
                            <li>• Use the App Password here, not your regular Gmail password</li>
                            <li>• This email will send password resets and notifications</li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
                      <button
                        type="submit"
                        data-testid="save-email-button"
                        className="btn-primary"
                      >
                        Save Email Configuration
                      </button>
                      <button
                        type="button"
                        onClick={() => cancelEdit('email')}
                        data-testid="cancel-email-button"
                        className="btn-secondary"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="form-label">Gmail Username</label>
                      <p className="text-gray-900" data-testid="config-gmail-username-display">
                        {adminConfig.gmail_username || (
                          <span className="text-gray-500 italic">Not configured</span>
                        )}
                      </p>
                    </div>

                    <div>
                      <label className="form-label">Gmail App Password</label>
                      <p className="text-gray-900 font-mono text-sm" data-testid="config-gmail-app-password-display">
                        {adminConfig.gmail_app_password ? (
                          <span>
                            ••••••••••••••••
                            <span className="text-green-600 ml-2">✓ Configured</span>
                          </span>
                        ) : (
                          <span className="text-gray-500 italic font-sans">Not configured</span>
                        )}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Configuration Status Summary */}
              <div className="card">
                <div className="card-header">
                  <h3 className="text-lg font-medium text-gray-900">Configuration Status</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center">
                      <span className="mr-2">📞</span>
                      <span className="font-medium">Contact Details</span>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-sm font-medium ${
                      adminConfig.contact_email && adminConfig.contact_phone 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {adminConfig.contact_email && adminConfig.contact_phone ? '✓ Complete' : '✗ Incomplete'}
                    </span>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center">
                      <span className="mr-2">💳</span>
                      <span className="font-medium">Stripe Config</span>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-sm font-medium ${
                      adminConfig.stripe_publishable_key && adminConfig.stripe_secret_key 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {adminConfig.stripe_publishable_key && adminConfig.stripe_secret_key ? '✓ Complete' : '✗ Incomplete'}
                    </span>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center">
                      <span className="mr-2">📧</span>
                      <span className="font-medium">Email Config</span>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-sm font-medium ${
                      adminConfig.gmail_username && adminConfig.gmail_app_password 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {adminConfig.gmail_username && adminConfig.gmail_app_password ? '✓ Complete' : '✗ Incomplete'}
                    </span>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>Last Updated:</span>
                    <span data-testid="config-updated-display">
                      {adminConfig.updated_at ? new Date(adminConfig.updated_at).toLocaleString() : 'Never'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Deployment Tab */}
          {activeTab === 'deployment' && (
            <div className="space-y-6">
              {/* Confirmation Modal */}
              {showIndexingConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                  <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
                    <div className="p-6">
                      <div className="flex items-center space-x-3 mb-4">
                        <span className="text-3xl">🚨</span>
                        <h3 className="text-xl font-bold text-gray-900">Production Database Indexing</h3>
                      </div>
                      
                      <p className="text-gray-700 mb-4">
                        This will create database indexes in your <strong>PRODUCTION</strong> environment.
                      </p>
                      
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                        <h4 className="font-semibold text-green-800 mb-2">✅ Benefits:</h4>
                        <ul className="text-sm text-green-700 space-y-1">
                          <li>• 10-20x faster menu loading</li>
                          <li>• Better performance for customers</li>
                          <li>• Improved concurrent user handling</li>
                        </ul>
                      </div>
                      
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                        <h4 className="font-semibold text-blue-800 mb-2">ℹ️ This operation is SAFE:</h4>
                        <ul className="text-sm text-blue-700 space-y-1">
                          <li>• Takes 2-5 minutes to complete</li>
                          <li>• May cause slight temporary performance impact</li>
                          <li>• Permanently improves database performance</li>
                          <li>• No data will be modified or deleted</li>
                        </ul>
                      </div>
                      
                      <div className="flex space-x-3">
                        <button
                          onClick={runProductionIndexing}
                          className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-lg font-medium transition-colors duration-200"
                        >
                          Continue
                        </button>
                        <button
                          onClick={cancelIndexing}
                          className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-3 rounded-lg font-medium transition-colors duration-200"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Header */}
              <div className="card">
                <div className="card-header">
                  <div className="flex items-center space-x-2">
                    <span className="text-2xl">🚀</span>
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900">Production Deployment</h2>
                      <p className="text-gray-600 text-sm mt-1">Optimize your production database performance</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Database Optimization Section */}
              <div className="card">
                <div className="card-header">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <span className="text-lg">⚡</span>
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">Database Performance Optimization</h3>
                        <p className="text-sm text-gray-600 mt-1">Create database indexes for faster query performance</p>
                      </div>
                    </div>
                    {!deploymentStatus.indexing && !deploymentStatus.results && (
                      <button
                        onClick={handleIndexingButtonClick}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-colors duration-200 flex items-center space-x-2"
                      >
                        <span>⚡</span>
                        <span>Create Production Indexes</span>
                      </button>
                    )}
                  </div>
                </div>

                <div className="p-6 space-y-4">
                  {/* Performance Benefits */}
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h4 className="font-medium text-green-800 mb-2">🚀 Expected Performance Improvements:</h4>
                    <ul className="text-sm text-green-700 space-y-1">
                      <li>• <strong>Menu loading:</strong> 10-20x faster (from 2-5 seconds to 50-200ms)</li>
                      <li>• <strong>Restaurant queries:</strong> 5-10x faster dashboard performance</li>
                      <li>• <strong>Order management:</strong> 8-15x faster order processing</li>
                      <li>• <strong>Concurrent users:</strong> 50-100x better handling under load</li>
                    </ul>
                  </div>

                  {/* Current Status */}
                  {deploymentStatus.indexing && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-center space-x-3">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                        <div>
                          <h4 className="font-medium text-blue-800">Creating Database Indexes...</h4>
                          <p className="text-sm text-blue-700 mt-1">{deploymentStatus.progress}</p>
                          <p className="text-xs text-blue-600 mt-2">This may take 2-5 minutes. Please do not close this page.</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Success Results */}
                  {deploymentStatus.results && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-start space-x-3">
                        <span className="text-green-600 text-xl">✅</span>
                        <div className="flex-1">
                          <h4 className="font-medium text-green-800">Production Database Optimization Complete!</h4>
                          <div className="mt-3 space-y-2">
                            <div className="text-sm text-green-700">
                              <strong>Indexes Created:</strong> {deploymentStatus.results.indexes_created || 'Multiple'} database indexes
                            </div>
                            {deploymentStatus.results.performance_test && (
                              <div className="text-sm text-green-700">
                                <strong>Performance Test:</strong> {deploymentStatus.results.performance_test.query_time || 'Fast'} query response time
                              </div>
                            )}
                            <div className="text-sm text-green-700">
                              <strong>Status:</strong> Your production database is now optimized for high performance!
                            </div>
                          </div>
                          <button
                            onClick={resetDeploymentStatus}
                            className="mt-3 text-sm text-green-600 hover:text-green-800 underline"
                          >
                            Clear Status
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Error Display */}
                  {deploymentStatus.error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-start space-x-3">
                        <span className="text-red-600 text-xl">❌</span>
                        <div className="flex-1">
                          <h4 className="font-medium text-red-800">Indexing Failed</h4>
                          <p className="text-sm text-red-700 mt-1">{deploymentStatus.error}</p>
                          <div className="mt-3 text-sm text-red-600">
                            <p><strong>Common solutions:</strong></p>
                            <ul className="list-disc list-inside mt-1 space-y-1">
                              <li>Check database connectivity</li>
                              <li>Verify admin permissions</li>
                              <li>Ensure production environment is accessible</li>
                              <li>Try again during low-traffic period</li>
                            </ul>
                          </div>
                          <button
                            onClick={resetDeploymentStatus}
                            className="mt-3 text-sm text-red-600 hover:text-red-800 underline"
                          >
                            Clear Error & Retry
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Safety Information */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-800 mb-2">🛡️ Safety Information:</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Database indexing is a <strong>safe operation</strong> that only adds performance indexes</li>
                      <li>• No existing data is modified or deleted during this process</li>
                      <li>• Indexes are automatically maintained by MongoDB as data changes</li>
                      <li>• Operation typically takes 2-5 minutes depending on database size</li>
                      <li>• Can be performed during normal business hours with minimal impact</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Future Deployment Features */}
              <div className="card">
                <div className="card-header">
                  <div className="flex items-center space-x-3">
                    <span className="text-lg">🔮</span>
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">Future Deployment Features</h3>
                      <p className="text-sm text-gray-600 mt-1">Additional optimization tools coming soon</p>
                    </div>
                  </div>
                </div>

                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="border border-gray-200 rounded-lg p-4 opacity-60">
                      <div className="flex items-center space-x-2 mb-2">
                        <span>🗄️</span>
                        <span className="font-medium text-gray-700">Database Backup</span>
                        <span className="text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded">Coming Soon</span>
                      </div>
                      <p className="text-sm text-gray-600">One-click production database backup</p>
                    </div>

                    <div className="border border-gray-200 rounded-lg p-4 opacity-60">
                      <div className="flex items-center space-x-2 mb-2">
                        <span>📊</span>
                        <span className="font-medium text-gray-700">Performance Monitor</span>
                        <span className="text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded">Coming Soon</span>
                      </div>
                      <p className="text-sm text-gray-600">Real-time database performance monitoring</p>
                    </div>

                    <div className="border border-gray-200 rounded-lg p-4 opacity-60">
                      <div className="flex items-center space-x-2 mb-2">
                        <span>🔄</span>
                        <span className="font-medium text-gray-700">Auto-Optimization</span>
                        <span className="text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded">Coming Soon</span>
                      </div>
                      <p className="text-sm text-gray-600">Automated performance optimization</p>
                    </div>

                    <div className="border border-gray-200 rounded-lg p-4 opacity-60">
                      <div className="flex items-center space-x-2 mb-2">
                        <span>🚀</span>
                        <span className="font-medium text-gray-700">Code Deployment</span>
                        <span className="text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded">Coming Soon</span>
                      </div>
                      <p className="text-sm text-gray-600">One-click application deployment</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Remove Admin Confirmation Modal */}
      {showRemoveConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-4 sm:p-6 max-w-sm mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Confirm Remove Admin
            </h3>
            <p className="text-gray-600 mb-6 text-sm sm:text-base">
              Are you sure you want to remove admin user: <strong className="break-all">{userToRemove}</strong>?
              This action cannot be undone.
            </p>
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4 justify-end">
              <button
                onClick={cancelRemoveAdmin}
                className="btn-secondary"
                data-testid="cancel-remove-admin"
              >
                Cancel
              </button>
              <button
                onClick={confirmRemoveAdmin}
                className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
                data-testid="confirm-remove-admin"
              >
                Remove Admin
              </button>
            </div>
          </div>
        </div>
      )}

      <TestPaymentModal
        isOpen={showTestPayment}
        onClose={() => setShowTestPayment(false)}
        apiClient={apiClient}
        createEndpoint="/admin/stripe-test-payment"
        title="Test Platform Stripe Payment (1 USD)"
      />
    </div>
  );
};

export default AdminDashboard;